import React, { useState } from 'react';
import { 
  Search, 
  Package, 
  MapPin, 
  Calendar, 
  AlertTriangle,
  Plus,
  Eye,
  Edit,
  Trash2,
  Filter
} from 'lucide-react';

const ComponentInventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  const componentInventory = [
    {
      id: 1,
      sku: 'NAL-001',
      name: 'Naloxone Nasal Spray 4mg',
      category: 'Active Ingredient',
      currentStock: 2400,
      minStock: 500,
      maxStock: 5000,
      location: 'A1-B2-C1',
      lots: [
        { lotNumber: 'LOT-2024-001', quantity: 1200, expiry: '2025-12-31', supplier: 'Emergent BioSolutions' },
        { lotNumber: 'LOT-2024-007', quantity: 1200, expiry: '2025-11-30', supplier: 'Emergent BioSolutions' }
      ],
      unitCost: 15.50,
      status: 'In Stock'
    },
    {
      id: 2,
      sku: 'INST-002',
      name: 'Instruction Cards (EN/FR)',
      category: 'Documentation',
      currentStock: 1500,
      minStock: 300,
      maxStock: 3000,
      location: 'B1-C2-D1',
      lots: [
        { lotNumber: 'LOT-2024-002', quantity: 800, expiry: '2025-11-30', supplier: 'Canadian Health Print' },
        { lotNumber: 'LOT-2024-008', quantity: 700, expiry: '2025-10-15', supplier: 'Canadian Health Print' }
      ],
      unitCost: 0.85,
      status: 'In Stock'
    },
    {
      id: 3,
      sku: 'FACE-003',
      name: 'Face Shield/Barrier',
      category: 'Safety Equipment',
      currentStock: 800,
      minStock: 600,
      maxStock: 2000,
      location: 'C1-D2-E1',
      lots: [
        { lotNumber: 'LOT-2024-003', quantity: 800, expiry: '2027-10-15', supplier: 'Safety First Medical' }
      ],
      unitCost: 2.25,
      status: 'In Stock'
    },
    {
      id: 4,
      sku: 'GLOV-004',
      name: 'Nitrile Gloves (Pairs)',
      category: 'PPE',
      currentStock: 350,
      minStock: 400,
      maxStock: 1500,
      location: 'B2-C3-D2',
      lots: [
        { lotNumber: 'LOT-2024-004', quantity: 350, expiry: '2025-09-30', supplier: 'ProGlove Canada' }
      ],
      unitCost: 0.22,
      status: 'Low Stock'
    },
    {
      id: 5,
      sku: 'NAL-002',
      name: 'Training Naloxone (Inert)',
      category: 'Training Materials',
      currentStock: 500,
      minStock: 200,
      maxStock: 1000,
      location: 'D1-E2-F1',
      lots: [
        { lotNumber: 'LOT-2024-005', quantity: 500, expiry: '2026-12-31', supplier: 'Medical Training Corp' }
      ],
      unitCost: 8.75,
      status: 'In Stock'
    },
    {
      id: 6,
      sku: 'TRAIN-006',
      name: 'Training Manual',
      category: 'Documentation',
      currentStock: 180,
      minStock: 150,
      maxStock: 500,
      location: 'B3-C1-D3',
      lots: [
        { lotNumber: 'LOT-2024-006', quantity: 180, expiry: '2026-09-30', supplier: 'Educational Resources Ltd' }
      ],
      unitCost: 3.50,
      status: 'In Stock'
    },
    {
      id: 7,
      sku: 'STET-007',
      name: 'Stethoscope',
      category: 'Medical Instruments',
      currentStock: 45,
      minStock: 50,
      maxStock: 200,
      location: 'C2-D1-E3',
      lots: [
        { lotNumber: 'LOT-2024-007', quantity: 45, expiry: '2027-01-01', supplier: 'MedDevice Canada' }
      ],
      unitCost: 24.99,
      status: 'Low Stock'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'In Stock': 'bg-green-100 text-green-800',
      'Low Stock': 'bg-yellow-100 text-yellow-800',
      'Critical': 'bg-red-100 text-red-800',
      'Out of Stock': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStockLevel = (current, min, max) => {
    if (current <= min) return 'Low Stock';
    if (current <= min * 0.5) return 'Critical';
    return 'In Stock';
  };

  const filteredItems = componentInventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || 
                         item.category.toLowerCase() === selectedFilter.toLowerCase() ||
                         getStockLevel(item.currentStock, item.minStock, item.maxStock).toLowerCase() === selectedFilter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Component Inventory</h1>
        <p className="text-gray-600">Manage raw materials and components for Naloxone kit assembly</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <select 
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Components</option>
              <option value="active ingredient">Active Ingredient</option>
              <option value="documentation">Documentation</option>
              <option value="safety equipment">Safety Equipment</option>
              <option value="ppe">PPE</option>
              <option value="training materials">Training Materials</option>
              <option value="low stock">Low Stock</option>
            </select>

            <button 
              onClick={() => alert('Add New Component\n\nThis feature will allow you to:\n• Add new component types (SKU, name, supplier)\n• Set reorder points\n• Configure expiry tracking\n• Upload component images\n\nAvailable after backend implementation.')}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Component</span>
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredItems.map((item) => {
          const stockLevel = getStockLevel(item.currentStock, item.minStock, item.maxStock);
          const stockPercentage = (item.currentStock / item.maxStock) * 100;
          
          return (
            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                    <p className="text-xs text-gray-500">{item.category}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(stockLevel)}`}>
                    {stockLevel}
                  </span>
                </div>

                {/* Stock Level */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Stock Level</span>
                    <span className="font-medium text-gray-900">
                      {item.currentStock.toLocaleString()} / {item.maxStock.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        stockLevel === 'Critical' ? 'bg-red-500' :
                        stockLevel === 'Low Stock' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Min: {item.minStock.toLocaleString()}</span>
                    <span>Unit Cost: ${item.unitCost}</span>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>Location: {item.location}</span>
                </div>
              </div>

              {/* Lot Information */}
              <div className="p-6">
                <h4 className="font-medium text-gray-900 mb-3">Lot Traceability</h4>
                <div className="space-y-3">
                  {item.lots.map((lot, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{lot.lotNumber}</span>
                        <span className="text-sm text-gray-600">{lot.quantity.toLocaleString()} units</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">Exp: {lot.expiry}</span>
                        </div>
                        <span className="text-gray-600">{lot.supplier}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Value */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Value:</span>
                    <span className="font-bold text-gray-900">
                      ${(item.currentStock * item.unitCost).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => alert(`View Component: ${item.sku}\n\n${item.name}\nSupplier: ${item.supplier}\nStock: ${item.quantity}\nLot: ${item.lot}\nExpiry: ${item.expiry}\n\nFull details after backend implementation.`)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-1 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View</span>
                  </button>
                  <button 
                    onClick={() => alert(`Edit Component: ${item.sku}\n\nThis feature will allow you to:\n• Update stock quantity\n• Add new lots\n• Update expiry dates\n• Change location\n\nAvailable after backend implementation.`)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-1 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                  <button 
                    onClick={() => {
                      if (window.confirm(`Delete Component: ${item.sku}?\n\nThis will remove:\n• Component record\n• All lot history\n• Stock data\n\n(Feature available after backend)`)) {
                        alert('Component deletion will be available after backend implementation.');
                      }
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComponentInventory;